import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import WhoUsesTheraTec from "@/components/WhoUsesTheraTec";
import HeroCarousel from "@/components/HeroCarousel";
import FinancingSection from "@/components/FinancingSection";
import TestimonialsWall from "@/components/TestimonialsWall";
import WhyChooseTheraTec from "@/components/WhyChooseTheraTec";
import AnimatedBackdrop from "@/components/AnimatedBackdrop";
import CountUp from "@/components/CountUp";
import MagneticButton from "@/components/MagneticButton";
import Partners from "@/components/Partners";
import { useReveal } from "@/hooks/useReveal";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export default function Home() {
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setProducts(r.data.products));
    api.get("/products").then((r) => setAllProducts(r.data.products));
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const heroImages = allProducts
    .filter((p) => p.image && !p.image.includes("images.unsplash.com"))
    .slice(0, 4)
    .map((p) => p.image);

  const [featuredRef, featuredRevealed] = useReveal();
  const [quoteRef, quoteRevealed] = useReveal();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="min-h-screen relative flex items-end pt-20 overflow-hidden bg-[#2A2622]">
        {/* Auto-cycling product carousel backdrop */}
        <HeroCarousel images={heroImages} interval={5000} />

        {/* Warm ambient orbs */}
        <div className="absolute inset-0 pointer-events-none z-[1]">
          <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-30 blur-[120px]" style={{ background: "radial-gradient(circle, #C9A961 0%, transparent 65%)", animation: "tt-orb-drift 18s ease-in-out infinite" }} />
          <div className="absolute top-1/3 right-0 w-[640px] h-[640px] rounded-full opacity-25 blur-[140px]" style={{ background: "radial-gradient(circle, #E8B87A 0%, transparent 65%)", animation: "tt-orb-drift 24s ease-in-out infinite reverse" }} />
          <div className="absolute -bottom-60 left-1/3 w-[720px] h-[720px] rounded-full opacity-20 blur-[160px]" style={{ background: "radial-gradient(circle, #8B6F47 0%, transparent 65%)", animation: "tt-orb-drift 30s ease-in-out infinite" }} />
        </div>

        {/* Depth gradient */}
        <div className="absolute inset-0 pointer-events-none z-[2]" style={{ background: "linear-gradient(180deg, rgba(42,38,34,0.30) 0%, rgba(42,38,34,0.10) 40%, rgba(42,38,34,0.60) 85%, rgba(42,38,34,0.85) 100%)" }} />

        {/* Left-side dark bleed for text legibility */}
        <div className="absolute inset-y-0 left-0 w-full lg:w-3/5 pointer-events-none z-[3]" style={{ background: "linear-gradient(90deg, rgba(28,24,22,0.55) 0%, rgba(28,24,22,0.20) 50%, transparent 100%)" }} />

        {/* Grain overlay */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.12] mix-blend-overlay z-[4]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

        {/* Vertical light streak */}
        <div className="absolute top-0 bottom-0 left-[65%] w-[1px] bg-gradient-to-b from-transparent via-[#E8B87A]/40 to-transparent pointer-events-none hidden lg:block z-[5]" />

        {/* Content */}
        <div className="relative z-10 max-w-[1440px] mx-auto w-full px-6 lg:px-12 pb-24 lg:pb-32 tt-depth-hero">
          <div
            className="max-w-3xl"
            style={{ transform: `translateY(${-scrollY * 0.15}px) translateZ(0)`, willChange: "transform" }}
          >
            <div className="flex items-center gap-4 tt-fade-up">
              <span className="w-10 h-px bg-[#C9A961]" />
              <div className="overline text-[#C9A961]">High End Wellness Tech · Est. 2024</div>
            </div>
            <h1
              data-testid="hero-headline"
              className="font-serif-editorial text-5xl sm:text-6xl lg:text-8xl text-[#FAF9F6] leading-[0.95] mt-8 tt-word-reveal"
              style={{ textShadow: "0 20px 40px rgba(0,0,0,0.3), 0 4px 12px rgba(0,0,0,0.2)", whiteSpace: "pre-wrap" }}
            >
              <span style={{ animationDelay: "150ms" }}>Premium </span>
              <em className="italic font-light">
                <span style={{ animationDelay: "320ms" }}>wellness </span>
                <span style={{ animationDelay: "470ms" }}>tech.</span>
              </em>
            </h1>
            <p className="mt-10 text-base lg:text-lg text-[#FAF9F6]/85 max-w-xl leading-relaxed tt-fade-up" style={{ animationDelay: "300ms" }}>
              A curated arsenal of hyperbaric, IV, cryo, and recovery equipment — clinical-grade performance, engineered for the aesthetic economy, priced to make premium accessible.
            </p>
            <div className="mt-12 flex flex-wrap gap-4 tt-fade-up" style={{ animationDelay: "450ms" }}>
              <MagneticButton>
                <a
                  href="#collection"
                  data-testid="hero-cta-shop"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("collection")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="tt-btn-primary tt-shimmer-sweep bg-[#FAF9F6] text-[#1C1C1C] hover:bg-[#F0EFEB]"
                >
                  Browse the Collection <ArrowUpRight size={16} strokeWidth={1.5} />
                </a>
              </MagneticButton>
              <MagneticButton>
                <Link to="/integration" data-testid="hero-cta-integration" className="tt-btn-outline tt-shimmer-sweep border-[#FAF9F6]/60 text-[#FAF9F6] hover:bg-[#FAF9F6] hover:text-[#1C1C1C]">Book a Consultation</Link>
              </MagneticButton>
            </div>

            {/* Scroll indicator */}
            <div className="mt-24 flex items-center gap-4 tt-fade-up" style={{ animationDelay: "700ms" }}>
              <div className="w-12 h-px bg-[#FAF9F6]/40" />
              <span className="overline text-[#FAF9F6]/60">Scroll</span>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="py-8 border-y border-[#E5E5E5] bg-[#F5F3EE] overflow-hidden tt-marquee-mask">
        <div className="tt-marquee">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex items-center gap-16 pr-16">
              {["Hyperbaric Recovery", "IV Wellness Suites", "Cryotherapy", "Ionic Detox", "Red-Light Therapy", "Whole-Body Vibration", "Concierge Installation"].map((t) => (
                <span key={t} className="overline whitespace-nowrap">— {t} —</span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* WHY CHOOSE THERATEC — trust slab immediately after hero */}
      <WhyChooseTheraTec />

      {/* FEATURED PRODUCTS — bento */}
      <section
        id="collection"
        ref={featuredRef}
        data-testid="featured-section"
        className={`relative max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-20 overflow-hidden tt-reveal ${featuredRevealed ? "is-revealed" : ""}`}
      >
        <AnimatedBackdrop variant="cream" />
        <div className="relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div className="max-w-xl">
            <div className="overline">The Collection</div>
            <h2 className="font-serif-editorial text-3xl lg:text-5xl mt-3 leading-tight">Signature equipment,<br /><em className="italic">curated with intent.</em></h2>
          </div>
          <Link to="/shop" data-testid="view-all-link" className="overline hover:text-[#5C5C5C] transition-colors duration-300 inline-flex items-center gap-2 group">
            View all equipment
            <ArrowUpRight size={14} strokeWidth={1.5} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          {(() => {
            // Consolidate all hyperbaric chambers into a single featured tile.
            const hyperbaric = products.filter((p) => p.category === "Hyperbaric Therapy");
            const nonHyperbaric = products
              .filter((p) => p.category !== "Hyperbaric Therapy")
              .map((p) =>
                p.category === "Red Light Therapy"
                  ? { ...p, slug: "red-light-collection", name: "TheraTec Red Light Beds", _hideprice: true, _linkTo: "/shop?category=Red Light Therapy" }
                  : p
              )
              .filter((p, i, arr) => p.slug !== "red-light-collection" || arr.findIndex((q) => q.slug === "red-light-collection") === i);
            const consolidated = hyperbaric.length
              ? [
                  {
                    slug: "hyperbaric-collection",
                    name: "TheraTec Hyperbaric Chambers",
                    category: "Hyperbaric Therapy",
                    image: "https://customer-assets-lqy194kg.emergentagent.net/job_luxe-med-spa/artifacts/yhiv6wpb_0C159328-7A95-4768-908B-66E3E5A457F5.PNG",
                    _hideprice: true,
                    _linkTo: "/hyperbaric",
                  },
                  ...nonHyperbaric,
                ]
              : nonHyperbaric;
            return consolidated;
          })().slice(0, 5).map((p, i, arr) => {
            const spans = [
              "md:col-span-4 md:row-span-2",
              "md:col-span-2",
              "md:col-span-2",
              arr.length > 4 ? "md:col-span-3" : "md:col-span-6",
              "md:col-span-3",
            ];
            const isBig = i === 0 || i >= 3;
            const linkTo = p._linkTo || `/product/${p.slug}`;
            const isExternal = !!p.external_url;
            const commonClass = `group relative overflow-hidden border border-[#E5E5E5] bg-[#F0EFEB] block h-full ${spans[i]} tt-reveal tt-card-lift ${featuredRevealed ? "is-revealed" : ""}`;
            const commonStyle = { transitionDelay: featuredRevealed ? `${i * 120}ms` : "0ms" };
            const CardInner = (
              <>
                <div className={`relative ${i === 0 ? "aspect-[4/3] md:aspect-auto md:h-full" : isBig ? (arr.length > 4 ? "aspect-[4/3]" : "aspect-[4/3] md:aspect-auto md:h-full") : "aspect-[4/5]"} overflow-hidden`}>
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-[1200ms] ease-out" />
                </div>
                <div className="absolute inset-0 flex flex-col justify-end p-6 lg:p-8 bg-gradient-to-t from-[#1C1C1C]/90 via-[#1C1C1C]/25 to-transparent">
                  <div className="overline text-[#FAF9F6]/70 group-hover:text-[#C9A961] transition-colors duration-500">{p.category}</div>
                  <div className="font-serif-editorial text-2xl lg:text-4xl text-[#FAF9F6] mt-2 leading-tight">{p.name}</div>
                  <div className="flex items-end justify-between mt-4">
                    <div className="text-[#FAF9F6] text-sm">{p._hideprice ? "" : fmt(p.price)}</div>
                    <ArrowUpRight size={20} strokeWidth={1.25} className="text-[#FAF9F6] group-hover:rotate-45 group-hover:scale-110 transition-transform duration-500" />
                  </div>
                </div>
                {/* Corner accent line */}
                <div className="absolute top-0 left-0 w-0 h-px bg-[#C9A961] group-hover:w-full transition-all duration-700 ease-out" />
              </>
            );
            return isExternal ? (
              <a
                key={p.slug}
                href={p.external_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`featured-product-${p.slug}`}
                className={commonClass}
                style={commonStyle}
              >
                {CardInner}
              </a>
            ) : (
              <Link
                key={p.slug}
                to={linkTo}
                data-testid={`featured-product-${p.slug}`}
                className={commonClass}
                style={commonStyle}
              >
                {CardInner}
              </Link>
            );
          })}
        </div>
        </div>
      </section>

      {/* PEER PROOF — testimonials after signature equipment */}
      <TestimonialsWall backdrop="usmap" />

      {/* WHO USES THERATEC */}
      <WhoUsesTheraTec />

      {/* PARTNERS */}
      <Partners />

      {/* FINANCING */}
      <FinancingSection />

      {/* WHITE-GLOVE DELIVERY — SHYFT PARTNERSHIP */}
      <section
        data-testid="home-shyft-section"
        className="bg-[#1C1917] text-[#FAF9F6] py-24 lg:py-32 overflow-hidden relative"
      >
        {/* Ambient champagne glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/3 w-[600px] h-[600px] rounded-full opacity-20 blur-[140px]" style={{ background: "radial-gradient(circle, #C9A961 0%, transparent 65%)" }} />
          <div className="absolute -bottom-40 right-1/4 w-[500px] h-[500px] rounded-full opacity-15 blur-[130px]" style={{ background: "radial-gradient(circle, #E8B87A 0%, transparent 65%)" }} />
        </div>

        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative z-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Copy */}
          <div className="lg:col-span-6">
            <div className="flex items-center gap-4 mb-8">
              <span className="w-10 h-px bg-[#C9A961]" />
              <div className="overline text-[#C9A961]">Nationwide White-Glove Delivery · Shyft Global Services</div>
            </div>
            <h2 className="font-serif-editorial text-4xl lg:text-6xl leading-[1.05]">
              White-glove delivery,<br />
              <em className="italic">nationwide.</em>
            </h2>
            <p className="mt-8 text-base lg:text-lg text-[#FAF9F6]/75 leading-relaxed max-w-xl">
              Every TheraTec order ships through <span className="text-[#FAF9F6] font-medium">Shyft Global Services</span>, our fulfillment partner — one of the most extensive medical field-service networks in North America. From crate to calibration, your equipment is escorted the entire way.
            </p>

            {/* Stat pills */}
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-[#333] pt-8">
              <div>
                <div className="font-serif-editorial text-3xl lg:text-4xl"><CountUp to="99%" /></div>
                <div className="mt-2 text-xs text-[#A8A096] leading-relaxed">US & Canada population coverage</div>
              </div>
              <div>
                <div className="font-serif-editorial text-3xl lg:text-4xl"><CountUp to="133+" /></div>
                <div className="mt-2 text-xs text-[#A8A096] leading-relaxed">NA forward stocking locations</div>
              </div>
              <div>
                <div className="font-serif-editorial text-3xl lg:text-4xl">Global</div>
                <div className="mt-2 text-xs text-[#A8A096] leading-relaxed">Partner network + badged resources</div>
              </div>
            </div>

            <MagneticButton>
              <Link to="/integration" data-testid="home-shyft-cta" className="mt-10 inline-flex tt-btn-outline border-[#FAF9F6]/60 text-[#FAF9F6] hover:bg-[#FAF9F6] hover:text-[#1C1C1C]">Learn About Integration</Link>
            </MagneticButton>
          </div>

          {/* Map */}
          <div className="lg:col-span-6" data-testid="home-shyft-map">
            <div className="relative overflow-hidden border border-[#333]">
              <img
                src="https://customer-assets.emergentagent.com/job_luxe-med-spa/artifacts/kfin0oie_IMG_1265.jpg"
                alt="Shyft North America Field Services Technicians coverage map — 99% US and Canada population coverage, 133+ forward stocking locations"
                className="w-full h-auto max-h-[440px] object-contain block bg-[#0A0908]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section
        ref={quoteRef}
        className={`relative max-w-[1440px] mx-auto px-6 lg:px-12 py-24 lg:py-32 text-center overflow-hidden tt-reveal ${quoteRevealed ? "is-revealed" : ""}`}
      >
        <AnimatedBackdrop variant="sand" />
        <div className="relative z-10 max-w-4xl mx-auto">
          <div className="overline">Client, Beverly Hills</div>
          <blockquote className="font-serif-editorial text-3xl lg:text-5xl leading-tight mt-8 italic">
            "TheraTec doesn't ship you a machine. They install a revenue line."
          </blockquote>
          <div className="mt-8 overline">— Dr. Amara Voss, Founder · Halcyon Aesthetics</div>
        </div>
      </section>
    </div>
  );
}
